import React, { useState } from 'react';
import { calculateBMI, calculateFFMI } from '../utils/bodyMetrics';
import ModalBackdrop from './ModalBackdrop';

// "[What's this?]" trigger + "About BMI" popup with the BMI scale table.
// Accepts the profile so it can compute and display the user's BMI and
// highlight the matching category row. Dismissal (backdrop click, ESC key,
// Close button) is handled by ModalBackdrop.
function BMIPopup({ profile }) {
  const [show, setShow] = useState(false);

  return (
    <>
      <span className="bmi-link" onClick={() => setShow(true)}>[What's this?]</span>
      {show && (
        <ModalBackdrop onClose={() => setShow(false)}>
          <div className="popup-content">
            <h3>About BMI</h3>
            <p>
              Body Mass Index (BMI) is a measure of body fat based on your weight and height. It's calculated as (weight in pounds * 703) / (height in inches)^2. {profile.currentWeight && profile.height ? `Your BMI is ${calculateBMI(profile).toFixed(1)}.` : 'Enter your weight and height to see your BMI.'}
            </p>
            <table className="bmi-scale">
              <thead>
                <tr>
                  <th>BMI Range</th>
                  <th>Category</th>
                </tr>
              </thead>
              <tbody>
                <tr className={calculateBMI(profile) < 18.5 ? 'highlight' : ''}>
                  <td>Below 18.5</td>
                  <td>Underweight</td>
                </tr>
                <tr className={calculateBMI(profile) >= 18.5 && calculateBMI(profile) <= 24.9 ? 'highlight' : ''}>
                  <td>18.5–24.9</td>
                  <td>Healthy</td>
                </tr>
                <tr className={calculateBMI(profile) >= 25.0 && calculateBMI(profile) <= 29.9 ? 'highlight' : ''}>
                  <td>25.0–29.9</td>
                  <td>Overweight</td>
                </tr>
                <tr className={calculateBMI(profile) >= 30.0 ? 'highlight' : ''}>
                  <td>30.0 or higher</td>
                  <td>Obese</td>
                </tr>
              </tbody>
            </table>
            <p>
              {profile.currentWeight && profile.height ? `Your BMI falls in the ${calculateBMI(profile) < 18.5 ? 'Underweight' : calculateBMI(profile) <= 24.9 ? 'Healthy' : calculateBMI(profile) <= 29.9 ? 'Overweight' : 'Obese'} range.` : ''} These categories are general guidelines—talk to a doctor for advice tailored to you.
            </p>
            {profile.currentWeight && profile.height && calculateBMI(profile) >= 25.0 && (
              <p className="encouragement-message">
                Your journey's just beginning, and I'm here to help! Let's track your workouts, log your meals (coming soon), and make progress together—one step at a time.
              </p>
            )}
            {calculateFFMI(profile) !== null && (() => {
              const ffmi = calculateFFMI(profile);
              return (
                <>
                  <h4 style={{ marginTop: '12px', marginBottom: '8px' }}>FFMI Categories</h4>
                  <table className="bmi-scale">
                    <thead>
                      <tr>
                        <th>FFMI Range</th>
                        <th>Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className={ffmi < 17 ? 'highlight' : ''}>
                        <td>Under 17</td>
                        <td>Below Average</td>
                      </tr>
                      <tr className={ffmi >= 17 && ffmi < 19 ? 'highlight' : ''}>
                        <td>17–19</td>
                        <td>Average</td>
                      </tr>
                      <tr className={ffmi >= 19 && ffmi < 21 ? 'highlight' : ''}>
                        <td>19–21</td>
                        <td>Above Average</td>
                      </tr>
                      <tr className={ffmi >= 21 && ffmi < 23 ? 'highlight' : ''}>
                        <td>21–23</td>
                        <td>Excellent</td>
                      </tr>
                      <tr className={ffmi >= 23 && ffmi < 26 ? 'highlight' : ''}>
                        <td>23–26</td>
                        <td>Superior</td>
                      </tr>
                      <tr className={ffmi >= 26 ? 'highlight' : ''}>
                        <td>26+</td>
                        <td>Exceptional</td>
                      </tr>
                    </tbody>
                  </table>
                  <p style={{ fontSize: '0.9em', color: '#94a3b8', marginTop: '12px' }}>
                    Note: BMI may be less accurate for athletes with high muscle mass. If you've entered your body fat %, your FFMI is shown above.
                  </p>
                  <p>Your normalized FFMI is {ffmi.toFixed(1)}.</p>
                </>
              );
            })()}
            <button className="btn-secondary" onClick={() => setShow(false)}>Close</button>
          </div>
        </ModalBackdrop>
      )}
    </>
  );
}

export default BMIPopup;
